import type { BookingCancellationPolicy } from "@/lib/api/bookingCancellationPolicy";

/**
 * Openings' own default cancellation window: a business with its policy
 * enabled but no hours set is enforced at this many hours.
 */
export const DEFAULT_CANCELLATION_POLICY_HOURS = 6;

/** Said when the policy is unknown — never a guessed number of hours. */
export const UNKNOWN_POLICY_SENTENCE =
  "Late cancellations and no-shows may forfeit this payment under the cancellation policy.";

const plural = (count: number, unit: string) =>
  `${count} ${unit}${count === 1 ? "" : "s"}`;

/** `N hour(s)`, or `N day(s)` when the window is a whole number of days. */
export const cancellationWindowLabel = (hours: number): string =>
  hours > 0 && hours % 24 === 0
    ? plural(hours / 24, "day")
    : plural(hours, "hour");

/** "deposit" when part of the total is charged now, "payment" when all of it. */
export type UpfrontPaymentNoun = "deposit" | "payment";

/**
 * The refund sentence under the charge on the card step, from the business's
 * real Openings cancellation policy. Since Openings booking standard 2.0.0
 * (Sep 30, 2026) the Openings API refunds a customer's cancel automatically
 * before the window (always, with the window off) and keeps the payment
 * inside it or on a no-show, so this mirrors Openings'
 * `booking.payment.refundPolicy` wording. Never say "non-refundable".
 *
 * - `null`/`undefined` (unknown, or the lookup failed) → a generic sentence
 *   with no hours and no refund promise.
 * - disabled → a cancel is always refunded.
 * - enabled with no hours → Openings' default window (6 hours).
 * - enabled with 0 hours → any time before the appointment begins.
 * - enabled with an unusable number (negative, NaN, infinite) → unknown.
 *
 * edition-mobile's `features/booking/depositMath.ts` `policySentence` still
 * carries the older non-refundable wording (Openings `BF-DRIFT-052`); move it
 * to these strings in its own change.
 */
export const paymentPolicySentence = (
  policy: BookingCancellationPolicy | null | undefined,
  noun: UpfrontPaymentNoun = "payment",
): string => {
  if (!policy) return UNKNOWN_POLICY_SENTENCE;
  if (!policy.enabled) return `If you cancel, your ${noun} is refunded automatically.`;

  const hours = policy.hours ?? DEFAULT_CANCELLATION_POLICY_HOURS;
  if (!Number.isFinite(hours) || hours < 0) return UNKNOWN_POLICY_SENTENCE;
  const advance =
    hours === 0
      ? "at any time before your appointment begins"
      : `at least ${cancellationWindowLabel(hours)} in advance`;
  return `Cancel ${advance} and your ${noun} is refunded automatically. Later cancellations and no-shows keep the ${noun}.`;
};

/**
 * Openings, Inc. collects these charges as the business's payment collection
 * agent, so every card step says so and names the business the charge is for.
 * The statement line stays true whether the charge settles on the Openings
 * platform ("OPENINGS* business") or `on_behalf_of` the business (its own
 * descriptor, openings-client PR #420).
 */
export const collectionAgentNotice = (businessName: string): string =>
  `Openings, Inc. collects card payments on behalf of ${businessName}. Your card statement will show the business name.`;
